/** Comprehensive profile management.
 *  Tabs: Personal · Corporate · Security · Billing · Team · Notifications */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { NavBar } from "@/components/NavBar";
import { api, setAuth } from "@/lib/api";
import { T } from "@/lib/theme";
import { QRCodeSVG } from "qrcode.react";

const C = T.dark;

const TABS = [
  { id: "personal", label: "Personal" },
  { id: "corporate", label: "Corporate" },
  { id: "security", label: "Security" },
  { id: "billing", label: "Billing" },
  { id: "team", label: "Team" },
  { id: "notifications", label: "Notifications" },
];

const tabBtn = (active) => ({
  padding: "8px 14px", borderRadius: 8,
  border: `1px solid ${active ? C.cyan + "44" : C.border}`,
  background: active ? C.cyan + "12" : "transparent",
  color: active ? C.cyan : C.muted, cursor: "pointer", fontSize: 13,
  fontWeight: active ? 700 : 500,
});
const card = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 };
const label = { display: "block", color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 };
const input = { width: "100%", padding: "11px 14px", background: C.bg, border: `1px solid ${C.border}`,
  color: C.text, borderRadius: 8, fontFamily: "inherit", fontSize: 13, marginBottom: 16, outline: "none" };
const btn = {
  padding: "11px 22px", borderRadius: 8, border: "none", cursor: "pointer",
  fontSize: 13, fontWeight: 700, background: T.greenAccent, color: "#fff",
};

export default function Profile() {
  const { user, reload } = useAuth();
  const [tab, setTab] = useState("personal");
  const [form, setForm] = useState({});
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [billing, setBilling] = useState([]);
  const [team, setTeam] = useState([]);
  const [orgName, setOrgName] = useState("");
  const [invite, setInvite] = useState({ email: "", full_name: "", role: "advertiser" });
  const [tempPwd, setTempPwd] = useState("");
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(false);
  // MFA state
  const [mfaSetup, setMfaSetup] = useState(null); // {secret, otpauth_uri}
  const [mfaCode, setMfaCode] = useState("");
  // Daily digest hour (Blueprint Batch B)
  const [digestHour, setDigestHour] = useState(18);
  const [digestHourSet, setDigestHourSet] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  useEffect(() => {
    if (!user) return;
    setForm({
      full_name: user.full_name || "",
      organisation: user.organisation || "",
      phone: user.phone || "",
      country: user.country || "Ghana",
      avatar_url: user.avatar_url || "",
      registration_no: user.registration_no || "",
      vat_no: user.vat_no || "",
      mailing_address: user.mailing_address || "",
      billing_email: user.billing_email || "",
      notify_email: user.notify_email !== false,
      notify_sms: user.notify_sms || false,
      notify_certificates: user.notify_certificates !== false,
      notify_weekly_charts: user.notify_weekly_charts !== false,
    });
  }, [user]);

  useEffect(() => {
    if (tab === "billing") api.get("/profile/billing").then((r) => setBilling(r.data.payments || [])).catch(() => {});
    if (tab === "team") api.get("/profile/team").then((r) => { setTeam(r.data.team || []); setOrgName(r.data.organisation || ""); }).catch(() => {});
    if (tab === "notifications") {
      api.get("/me/preferences").then((r) => {
        setDigestHour(typeof r.data.digest_hour === "number" ? r.data.digest_hour : 18);
        setDigestHourSet(!!r.data.digest_hour_set);
      }).catch(() => {});
    }
  }, [tab]);

  const save = async () => {
    setLoading(true);
    try {
      // Notifications tab owns the daily digest hour preference (Blueprint
      // Batch B). It does NOT touch /profile fields, so save it independently
      // — the legacy PATCH /profile path 422s on empty billing_email and was
      // swallowing this preference write.
      if (tab === "notifications") {
        await api.put("/me/preferences", { digest_hour: digestHour });
        await api.patch("/profile", {
          notify_email: form.notify_email,
          notify_sms: form.notify_sms,
          notify_certificates: form.notify_certificates,
          notify_weekly_charts: form.notify_weekly_charts,
        });
        const firstSave = !digestHourSet;
        setDigestHourSet(true);
        showToast(firstSave
          ? `Saved · daily digest at ${String(digestHour).padStart(2, "0")}:00 local`
          : "Notifications saved");
        await reload?.();
        return;
      }
      await api.patch("/profile", form);
      await reload?.();
      showToast("Profile saved");
    } catch (e) {
      showToast(e.response?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  const changePassword = async () => {
    if (pwd.new_password.length < 8) return showToast("New password must be at least 8 characters");
    setLoading(true);
    try {
      const r = await api.post("/profile/change-password", pwd);
      // Update local tokens
      if (r.data?.access_token) {
        setAuth({ access_token: r.data.access_token, refresh_token: r.data.refresh_token });
      }
      setPwd({ current_password: "", new_password: "" });
      showToast("Password updated");
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const inviteMember = async () => {
    if (!invite.email || !invite.full_name) return showToast("Email and name required");
    setLoading(true);
    setTempPwd("");
    try {
      const r = await api.post("/profile/team/invite", invite);
      setTempPwd(r.data.temp_password);
      setTeam((t) => [...t, r.data.user]);
      setInvite({ email: "", full_name: "", role: "advertiser" });
      showToast("Member invited");
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };
  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.email} from ${orgName}?`)) return;
    try { await api.delete(`/profile/team/${m.id}`); setTeam((t) => t.filter((x) => x.id !== m.id)); showToast("Removed"); }
    catch (e) { showToast(e.response?.data?.detail || "Failed"); }
  };

  // ─────── MFA ───────
  const startMfa = async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/enable-mfa");
      setMfaSetup(r.data);
    } catch (e) { showToast(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  const confirmMfa = async () => {
    if (mfaCode.length !== 6) return showToast("Enter the 6-digit code");
    setLoading(true);
    try {
      await api.post("/auth/verify-mfa", { code: mfaCode });
      showToast("MFA enabled");
      setMfaSetup(null);
      setMfaCode("");
      await reload?.();
    } catch (e) { showToast(e.response?.data?.detail || "Invalid code"); }
    finally { setLoading(false); }
  };
  const disableMfa = async () => {
    if (mfaCode.length !== 6) return showToast("Enter the 6-digit code");
    setLoading(true);
    try {
      await api.post("/auth/disable-mfa", { code: mfaCode });
      showToast("MFA disabled");
      setMfaCode("");
      await reload?.();
    } catch (e) { showToast(e.response?.data?.detail || "Invalid code"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="profile-page" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <style>{`body { margin:0; background:${C.bg} } @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&display=swap')`}</style>
      <NavBar dark />

      <div style={{ maxWidth: 880, margin: "0 auto", padding: "28px 24px" }}>
        <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 28, fontWeight: 800, margin: "0 0 6px" }}>
          Account Settings
        </h1>
        <p style={{ color: C.muted, margin: "0 0 22px", fontSize: 13 }}>
          {user?.email} · <span style={{ color: C.cyan }}>{user?.role}</span> · {user?.organisation || "no organisation"}
        </p>

        <div style={{ display: "flex", gap: 6, marginBottom: 22, flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <button key={t.id} data-testid={`profile-tab-${t.id}`} onClick={() => setTab(t.id)} style={tabBtn(tab === t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {toast && <div data-testid="profile-toast" style={{ position: "fixed", top: 90, right: 24, padding: "10px 18px",
          background: T.greenAccent, color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 100 }}>
          {toast}</div>}

        {tab === "personal" && (
          <div style={card}>
            <label style={label}>Full name</label>
            <input data-testid="profile-name" style={input} value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            <label style={label}>Phone</label>
            <input data-testid="profile-phone" style={input} value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+233244000000" />
            <label style={label}>Country</label>
            <select data-testid="profile-country" style={input} value={form.country || "Ghana"} onChange={(e) => setForm({ ...form, country: e.target.value })}>
              <option>Ghana</option><option>Nigeria</option><option>Cote d'Ivoire</option><option>Senegal</option>
            </select>
            <label style={label}>Avatar URL</label>
            <input data-testid="profile-avatar" style={input} value={form.avatar_url || ""} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://…" />
            <button data-testid="profile-save-personal" onClick={save} disabled={loading} style={btn}>{loading ? "Saving..." : "Save"}</button>
          </div>
        )}

        {tab === "corporate" && (
          <div style={card}>
            <label style={label}>Organisation / Station name</label>
            <input data-testid="profile-org" style={input} value={form.organisation || ""} onChange={(e) => setForm({ ...form, organisation: e.target.value })} />
            <label style={label}>Registration number</label>
            <input data-testid="profile-regno" style={input} value={form.registration_no || ""} onChange={(e) => setForm({ ...form, registration_no: e.target.value })} placeholder="CS123456789" />
            <label style={label}>VAT / TIN number</label>
            <input data-testid="profile-vat" style={input} value={form.vat_no || ""} onChange={(e) => setForm({ ...form, vat_no: e.target.value })} />
            <label style={label}>Billing email</label>
            <input data-testid="profile-billing-email" type="email" style={input} value={form.billing_email || ""} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} placeholder="invoices@company.com" />
            <label style={label}>Mailing / billing address</label>
            <textarea data-testid="profile-address" style={{ ...input, minHeight: 80, resize: "vertical" }} value={form.mailing_address || ""} onChange={(e) => setForm({ ...form, mailing_address: e.target.value })} placeholder="P.O. Box AC 234, Accra" />
            <button data-testid="profile-save-corporate" onClick={save} disabled={loading} style={btn}>{loading ? "Saving..." : "Save"}</button>
          </div>
        )}

        {tab === "security" && (
          <div style={card}>
            <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 14px" }}>Change password</h3>
            <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.6, margin: "0 0 16px" }}>
              You'll stay signed in on this device. Other devices and refresh tokens will be revoked.
            </p>
            <label style={label}>Current password</label>
            <input data-testid="profile-current-pwd" type="password" style={input} value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} />
            <label style={label}>New password (min 8 characters)</label>
            <input data-testid="profile-new-pwd" type="password" style={input} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} />
            <button data-testid="profile-change-pwd" onClick={changePassword} disabled={loading} style={btn}>
              {loading ? "Updating..." : "Update password"}
            </button>

            {/* ─── MFA ─── */}
            <div style={{ marginTop: 36, paddingTop: 24, borderTop: `1px solid ${C.border}` }}>
              <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Two-factor authentication (MFA)</h3>
              <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.6, margin: "0 0 16px" }}>
                {user?.mfa_enabled
                  ? "MFA is active. Your account requires a 6-digit code from your authenticator app at every sign-in."
                  : "Add a second sign-in step with Google Authenticator, Authy, or 1Password. Strongly recommended for admins and station managers."}
              </p>

              {!user?.mfa_enabled && !mfaSetup && (
                <button data-testid="mfa-enable-btn" onClick={startMfa} disabled={loading} style={btn}>
                  {loading ? "..." : "Enable MFA"}
                </button>
              )}

              {!user?.mfa_enabled && mfaSetup && (
                <div data-testid="mfa-setup" style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
                  <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>1. Scan this QR with Google Authenticator / Authy:</div>
                  <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                    <div data-testid="mfa-qr" style={{ background: "#fff", padding: 8, borderRadius: 8, lineHeight: 0 }}>
                      <QRCodeSVG value={mfaSetup.otpauth_uri} size={160} level="M" includeMargin={false} />
                    </div>
                    <div style={{ flex: "1 1 200px" }}>
                      <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>OR enter this secret manually:</div>
                      <code data-testid="mfa-secret" style={{ display: "block", background: "#000", color: C.cyan, fontSize: 13,
                        padding: "10px 12px", borderRadius: 6, wordBreak: "break-all", fontFamily: "'DM Mono',monospace" }}>
                        {mfaSetup.secret}
                      </code>
                    </div>
                  </div>
                  <div style={{ color: C.muted, fontSize: 12, margin: "16px 0 8px" }}>2. Enter the 6-digit code shown in your app to confirm:</div>
                  <input data-testid="mfa-code" maxLength={6} inputMode="numeric" value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    style={{ ...input, fontFamily: "'DM Mono',monospace", fontSize: 18, letterSpacing: "0.4em", textAlign: "center", marginBottom: 12 }} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button data-testid="mfa-cancel" onClick={() => { setMfaSetup(null); setMfaCode(""); }}
                      style={{ ...btn, background: "transparent", color: C.muted, border: `1px solid ${C.border}` }}>Cancel</button>
                    <button data-testid="mfa-confirm" onClick={confirmMfa} disabled={loading || mfaCode.length !== 6} style={btn}>
                      {loading ? "..." : "Activate MFA"}
                    </button>
                  </div>
                </div>
              )}

              {user?.mfa_enabled && (
                <div data-testid="mfa-active" style={{ background: T.greenAccent + "12", border: `1px solid ${T.greenAccent}44`,
                  borderRadius: 10, padding: 18 }}>
                  <div style={{ color: T.greenAccent, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
                    ✓ MFA is active on this account
                  </div>
                  <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>
                    To disable MFA, enter your current 6-digit authenticator code:
                  </div>
                  <input data-testid="mfa-disable-code" maxLength={6} inputMode="numeric" value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    style={{ ...input, fontFamily: "'DM Mono',monospace", fontSize: 18, letterSpacing: "0.4em", textAlign: "center", marginBottom: 12 }} />
                  <button data-testid="mfa-disable-btn" onClick={disableMfa} disabled={loading || mfaCode.length !== 6}
                    style={{ ...btn, background: "#EF4444" }}>{loading ? "..." : "Disable MFA"}</button>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "billing" && (
          <div style={card}>
            <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 14px" }}>Billing history</h3>
            <div style={{ color: C.muted, fontSize: 13, marginBottom: 12 }}>Current plan: <b style={{ color: C.cyan }}>{user?.plan || "free"}</b></div>
            {billing.length === 0 && <div style={{ color: C.muted, padding: "20px 0", fontSize: 13 }}>No payments yet.</div>}
            {billing.map((p) => (
              <div key={p.reference} data-testid={`billing-${p.reference}`} style={{ display: "flex", justifyContent: "space-between",
                padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                <div>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{p.plan} plan · GH₵ {(p.amount_kobo / 100).toLocaleString()}</div>
                  <div style={{ color: C.muted, fontSize: 11, fontFamily: "monospace" }}>{p.reference}</div>
                </div>
                <div style={{ color: p.status === "success" ? T.greenAccent : "#F59E0B", fontSize: 12, fontWeight: 600 }}>
                  {p.status}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "team" && (
          <div style={card}>
            <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>{orgName || "Team"}</h3>
            <p style={{ color: C.muted, fontSize: 12, margin: "0 0 18px" }}>Invite colleagues to join your workspace. They'll inherit your organisation.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 140px", gap: 8, marginBottom: 8 }}>
              <input data-testid="team-email" placeholder="email@company.com" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} style={{ ...input, marginBottom: 0 }} />
              <input data-testid="team-name" placeholder="Full name" value={invite.full_name} onChange={(e) => setInvite({ ...invite, full_name: e.target.value })} style={{ ...input, marginBottom: 0 }} />
              <select data-testid="team-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} style={{ ...input, marginBottom: 0 }}>
                <option value="advertiser">Advertiser</option><option value="brand">Brand</option><option value="station_manager">Station mgr</option>
              </select>
            </div>
            <button data-testid="team-invite" onClick={inviteMember} disabled={loading} style={{ ...btn, marginBottom: 16 }}>
              {loading ? "Inviting..." : "Send invite"}
            </button>
            {tempPwd && (
              <div data-testid="team-temp-pwd" style={{ background: "#F59E0B22", color: "#F59E0B", border: "1px solid #F59E0B55",
                borderRadius: 8, padding: "10px 14px", fontSize: 12, marginBottom: 16, fontFamily: "monospace" }}>
                One-time password (share with member): <b>{tempPwd}</b>
              </div>
            )}
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>{team.length} members</div>
              {team.map((m) => (
                <div key={m.id} data-testid={`team-member-${m.id}`} style={{ display: "flex", justifyContent: "space-between",
                  padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{m.full_name}</div>
                    <div style={{ color: C.muted, fontSize: 11 }}>{m.email} · {m.role}</div>
                  </div>
                  <button data-testid={`team-remove-${m.id}`} onClick={() => removeMember(m)} style={{ padding: "6px 12px", background: "transparent",
                    border: `1px solid ${C.border}`, color: "#EF4444", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "notifications" && (
          <div style={card}>
            {[
              ["notify_email", "Account & security emails", "Sign-in alerts, password changes, claim verifications. Strongly recommended."],
              ["notify_sms", "SMS alerts via Hubtel", "For two-factor codes and station-claim verifications."],
              ["notify_certificates", "Transmission certificate notifications", "Email me whenever a campaign certificate is auto-issued."],
              ["notify_weekly_charts", "WaveScore Charts (weekly)", "Friday digest of top stations and songs across West Africa."],
            ].map(([key, name, desc]) => (
              <label key={key} data-testid={`notify-${key}`} style={{ display: "flex", gap: 14, padding: "14px 0",
                borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                  style={{ marginTop: 4, width: 16, height: 16, accentColor: T.greenAccent }} />
                <div>
                  <div style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{name}</div>
                  <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>{desc}</div>
                </div>
              </label>
            ))}

            {/* Daily digest hour (Blueprint Batch B) */}
            <div data-testid="digest-hour-row" style={{ display: "flex", gap: 14, padding: "16px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: 16, height: 16, marginTop: 4, borderRadius: 4, background: T.greenAccent + "22",
                color: T.greenAccent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>⏰</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>Daily digest delivery time</div>
                <div style={{ color: C.muted, fontSize: 12, marginTop: 3, marginBottom: 10 }}>
                  Your single-screen daily summary of campaign delivery, exceptions and growing stations.
                  {!digestHourSet && <> Defaults to <b style={{ color: T.greenAccent }}>18:00 (6 PM local)</b>; saved on your first click.</>}
                </div>
                <select data-testid="digest-hour-select" value={digestHour}
                  onChange={(e) => setDigestHour(Number(e.target.value))}
                  style={{ ...input, marginBottom: 0, width: 200 }}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, "0")}:00 — {h < 12 ? `${h || 12} AM` : `${h === 12 ? 12 : h - 12} PM`}
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: 8, fontSize: 11, color: digestHourSet ? T.greenAccent : C.muted }}>
                  {digestHourSet
                    ? `✓ Saved: digest fires daily at ${String(digestHour).padStart(2, "0")}:00 local.`
                    : "Click Save below to lock in this delivery time."}
                </div>
              </div>
            </div>

            <button data-testid="profile-save-notify" onClick={save} disabled={loading} style={{ ...btn, marginTop: 18 }}>{loading ? "Saving..." : "Save"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
